import React, { Dispatch, useEffect, useRef, useState } from 'react'

import { getClientProposalCardProps } from './client-proposal-cards.utils'
import ProposalAdvisorConversationChat from '../../organisms/proposal/proposal-advisor-conversation/ProposalAdvisorConversationChat'
import ProposalAdvisorConversationHint from '../../organisms/proposal/proposal-advisor-conversation/ProposalAdvisorConversationHint'
import { AdvisorConversation } from '../../organisms/proposal/proposal-advisor-conversation/types'
import { ClientProposalCard } from '../../..'
import {
  ClientProposalCardsFragment,
  ClientProposalUserActionEnum,
  ClientProposalVoteEnum,
  CurrencyEnum,
  HouseBookingTypeEnum,
  NonCompliantRulesFragment,
} from '../../../lib/graphql'

type AdvisorConversationProps = Readonly<{
  // obligé de passer ça sous forme de fonction car
  // impossible de gérer le state pour chaque proposal en dehors du packages ui.
  useAdvisorConversation?: () => AdvisorConversation & { unread?: number }
}>

export type ClientProposalCardsItemProps = ClientProposalCardsFragment &
  AdvisorConversationProps

const allFeatures = ['nudges'] as const
type Feature = (typeof allFeatures)[number]

export type ClientProposalCardsProps = {
  clientProposals: ClientProposalCardsItemProps[]
  hoveredProposalId?: string
  selectedProposalId?: string
  features?: Feature[]
  onProposalMouseEnter?: (id: string) => void
  onProposalMouseLeave?: () => void
  onHouseTitleClick?: (id: string) => void
  getFooter?: (
    userCurrentAction: ClientProposalUserActionEnum,
    houseBookingType: HouseBookingTypeEnum,
    clientProposalId: string,
    nonCompliantRules: NonCompliantRulesFragment,
    voteData: {
      upCount: number
      downCount: number
      userVote: ClientProposalVoteEnum | null
      voteForEmail: ClientProposalVoteEnum | null
    },
  ) => React.ReactNode
  disponibilityTag: (
    userCurrentAction: ClientProposalUserActionEnum,
  ) => React.ReactNode
  price: (
    userCurrentAction: ClientProposalUserActionEnum,
    houseBookingType: HouseBookingTypeEnum,
    priceWithoutVat: number,
    discountPriceWithoutVat: number,
    adults: number,
    currency: CurrencyEnum,
  ) => React.ReactNode
}

const ConversationWrapper = ({
  clientProposal,
  setShowChatForProposalId,
  showChatForProposalId,
}: {
  clientProposal: ClientProposalCardsItemProps
  showChatForProposalId?: string
  setShowChatForProposalId: Dispatch<string | undefined>
}) => {
  const { messages, unread, add, open } =
    clientProposal.useAdvisorConversation!()

  const onHintClick = () => {
    setShowChatForProposalId(clientProposal._id)
    open?.()
  }

  return (
    <>
      <ProposalAdvisorConversationHint
        onClick={onHintClick}
        unreadMessages={unread}
        lastMessage={(() => {
          const last = messages.findLast(
            (message) => message?.content && message?.id,
          )
          if (!last) {
            return undefined
          }
          return {
            content: last.content!,
            author: last.author,
            sentAt: last.sentAt,
          }
        })()}
      />
      {showChatForProposalId === clientProposal._id &&
        (() => {
          const { adults, startDate, endDate, house } = clientProposal
          return (
            <ProposalAdvisorConversationChat
              proposal={{
                adults: adults ?? 0,
                startDate: new Date(startDate),
                endDate: new Date(endDate),
                picture: house?.pictures[0]?.uri,
                title: house?.title ?? '',
              }}
              messages={messages}
              add={add}
              quit={() => setShowChatForProposalId(undefined)}
            />
          )
        })()}
    </>
  )
}

export const ClientProposalCards = (props: ClientProposalCardsProps) => {
  const { clientProposals, selectedProposalId } = props

  const cardRefs = useRef<Record<string, HTMLLIElement>>({})

  const [showChatForProposalId, setShowChatForProposalId] = useState<
    string | undefined
  >(undefined)

  useEffect(() => {
    if (!selectedProposalId) {
      return
    }

    const element = cardRefs.current[selectedProposalId]
    element?.scrollIntoView({
      block: 'nearest',
      inline: 'start',
      behavior: 'smooth',
    })
  }, [selectedProposalId])

  return (
    <ul className='flex min-w-0 flex-1 flex-col gap-8'>
      {clientProposals.map((clientProposal) => {
        const clientProposalCardProps = getClientProposalCardProps({
          ...props,
          clientProposal,
        })

        if (!clientProposalCardProps) {
          return (
            <li key={clientProposal._id} className='rounded bg-red-500 p-2'>
              <p>
                ⚠️ Erreur lors de la récupération de la suggestion:{' '}
                {clientProposal._id}
              </p>
            </li>
          )
        }

        return (
          <li
            className='flex flex-col gap-3 rounded-md bg-white'
            key={clientProposal._id}
            ref={(el) => {
              if (el) {
                cardRefs.current[clientProposal._id] = el
              }
            }}
          >
            <ClientProposalCard {...clientProposalCardProps} />

            {clientProposal.useAdvisorConversation && (
              <ConversationWrapper
                setShowChatForProposalId={setShowChatForProposalId}
                showChatForProposalId={showChatForProposalId}
                clientProposal={clientProposal}
              />
            )}
          </li>
        )
      })}
    </ul>
  )
}
